import { useState, useRef, ChangeEvent } from 'react';
import { ImportPanelProps } from './index';
import { todayStr, fmtHrs } from '@/lib/time';
import { parseLines, groupByDate, computeSessions } from '@/lib/parser';
import { mergeAndDeduplicate } from '@/lib/storage';

export type UseHooksParams = ImportPanelProps;

export const useHooks = ({ entries, onImport, onClearAll }: UseHooksParams) => {
  const [inputText, setInputText] = useState('');
  const [replaceMode, setReplaceMode] = useState(false);
  const [fallbackDate, setFallbackDate] = useState(todayStr());
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<{ date: string; displayHours: string }[] | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
    setError(null);
  };

  const handleDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFallbackDate(e.target.value);
  };

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      setInputText(text);
      setError(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!inputText.trim()) {
      setError('Textarea is empty. Please paste or upload your timelog.');
      return;
    }

    const parsed = parseLines(inputText, fallbackDate);
    if (parsed.length === 0) {
      setError('No valid IN/OUT lines found. Expected format: "IN | HH:MM | Name" or "OUT | HH:MM | Name"');
      setPreviewData(null);
      return;
    }

    const merged = mergeAndDeduplicate(entries, parsed, replaceMode);

    // Compute preview data for the newly imported days
    const parsedSessions = computeSessions(parsed);
    const parsedDayMap = groupByDate(parsedSessions);
    
    const preview = Object.entries(parsedDayMap)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([date, hrs]) => ({
        date,
        displayHours: fmtHrs(hrs)
      }));

    setPreviewData(preview);
    setError(null);
    setInputText('');

    const importedDates = Array.from(new Set(parsed.map(e => e.date)));
    await onImport(merged, importedDates);
  };

  const handleClearInput = () => {
    setInputText('');
    setError(null);
    setPreviewData(null);
  };

  const handleClearAll = async () => {
    if (confirm('Are you sure you want to delete all timelog entries? This action cannot be undone.')) {
      setPreviewData(null);
      setError(null);
      await onClearAll();
    }
  };

  return {
    inputText,
    fallbackDate,
    error,
    previewData,
    replaceMode,
    setReplaceMode,
    fileInputRef,
    handleTextChange,
    handleDateChange,
    handleFileUpload,
    handleImport,
    handleClearInput,
    handleClearAll
  };
};
